import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Drill, Menu, ArrowLeft, Home as HomeIcon, LayoutDashboard, Settings as SettingsIcon, ChevronRight, FilePlus, Eye, Check, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Drawer,
  DrawerTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer";
import { useIsMobile } from "@/hooks/use-mobile";
import { isAtLeast, ROLE_OPTIONS } from "@/lib/roles";
import { useFeaturePermissions } from "@/lib/FeaturePermissionContext";
import SyncStatusIndicator from "@/components/drill/SyncStatusIndicator";
import EndShiftButton from "@/components/drill/EndShiftButton";

function getNavItems(userRole, canUseFeature = () => true) {
  const isAdmin = isAtLeast(userRole, "admin");
  return [
    ...(canUseFeature("dashboard") ? [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }] : []),
    ...(canUseFeature("settings") ? [{ to: "/settings", label: "Settings", icon: SettingsIcon }] : []),
    ...(isAdmin && canUseFeature("document_builder") ? [{ to: "/document-builder", label: "Document Builder", icon: FilePlus }] : []),
    ...(isAdmin && canUseFeature("feature_permissions") ? [{ to: "/admin/feature-permissions", label: "Feature Permissions", icon: ShieldCheck }] : []),
    ...(canUseFeature("support") ? [{ to: "/tools/coming-soon-support", label: "Support", icon: ChevronRight }] : []),
  ];
}

function ViewAsDropdown() {
  const { viewAsRole, setViewAsRole, user } = useAuth();
  const options = ROLE_OPTIONS.filter((opt) => isAtLeast(user?.role, opt.value));
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="rounded-xl h-11 bg-white gap-2 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-100">
          <Eye className="w-4 h-4" /> View as
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map((opt) => (
          <DropdownMenuItem key={String(opt.value)} onClick={() => setViewAsRole(opt.value)}>
            <Check className={`w-4 h-4 ${viewAsRole === opt.value ? "opacity-100" : "opacity-0"}`} />
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MobileMenu() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();
  const { canUseFeature } = useFeaturePermissions();
  const navItems = getNavItems(effectiveRole, canUseFeature);

  const go = (to) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button variant="outline" className="rounded-xl h-11 bg-white gap-2 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-100">
          <Menu className="w-4 h-4" /> Menu
        </Button>
      </DrawerTrigger>
      <DrawerContent
        className="max-h-[calc(100dvh-1rem)] overflow-y-auto overscroll-contain dark:bg-slate-900 dark:border-slate-800"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)",
        }}
      >
        <DrawerHeader className="text-left">
          <DrawerTitle className="dark:text-slate-100">Navigation</DrawerTitle>
        </DrawerHeader>
        <div className="px-2 pb-2 space-y-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <DrawerClose asChild key={to}>
              <button
                onClick={() => go(to)}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-700 hover:bg-slate-100 active:bg-slate-200 transition-colors dark:text-slate-200 dark:hover:bg-slate-800 dark:active:bg-slate-700"
              >
                <Icon className="w-5 h-5" />
                <span className="flex-1 text-left font-medium">{label}</span>
                <ChevronRight className="w-4 h-4 text-slate-400 dark:text-slate-500" />
              </button>
            </DrawerClose>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

export default function AppHeader({ title, subtitle, action, backTo }) {
  const isMobile = useIsMobile();
  const { effectiveRole, user } = useAuth();
  const { canUseFeature } = useFeaturePermissions();
  const navItems = getNavItems(effectiveRole, canUseFeature);

  return (
    <header
      className="bg-white border-b border-slate-200 sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-white/80 dark:bg-slate-900 dark:border-slate-800 dark:supports-[backdrop-filter]:bg-slate-900/80 select-none"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          {backTo && (
            <Button asChild variant="ghost" size="icon" className="rounded-xl h-11 w-11 text-slate-600 dark:text-slate-300">
              <Link to={backTo} aria-label="Back">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
          )}
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center text-white dark:bg-slate-800">
              <Drill className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight dark:text-slate-100">
                {title}
              </h1>
              {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>}
            </div>
          </Link>
        </div>

        <div className="flex w-full min-w-0 items-center justify-end gap-2 sm:w-auto">
          {action}
          <EndShiftButton />
          <SyncStatusIndicator />
          {isAtLeast(user?.role, "admin") && <ViewAsDropdown />}
          {isMobile ? (
            <MobileMenu />
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-xl h-11 bg-white gap-2 dark:bg-slate-900 dark:border-slate-800 dark:text-slate-100">
                  <Menu className="w-4 h-4" /> Menu
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {navItems.map(({ to, label }) => (
                  <DropdownMenuItem asChild key={to}>
                    <Link to={to}>{label}</Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}